/* gwt-media-with-text-carousel.js
   Lightweight, accessible carousel web component (no dependencies).
*/
class GwtMediaWithTextCarousel extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-viewport]');
    this.track = this.querySelector('[data-track]');
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.headers = Array.from(this.querySelectorAll('[data-header-item]'));
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');

    this.count = this.slides.length;
    this.index = 0;

    this.loop = this.dataset.loop === 'true';
    this.autoplay = this.dataset.autoplay === 'true';

    this.autoplaySeconds = Math.max(3, parseInt(this.dataset.autoplaySeconds || '6', 10));
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

     this.videoAutoplay = this.dataset.videoAutoplay === 'true';

    if (!this.track || this.count <= 0) return;

    // If only one slide, no need to wire navigation.
    if (this.count <= 1) return;

    this._bind();
    this._apply(0, { focus: false, pauseMedia: false });

    // Autoplay respects reduced motion.
    if (this.autoplay && !this.prefersReducedMotion) this._startAutoplay();
  }

  disconnectedCallback() {
    this._stopAutoplay();
    this._unbindPointer?.();
  }

  _bind() {
    this.prevBtn?.addEventListener('click', () => this.go(this.index - 1));
    this.nextBtn?.addEventListener('click', () => this.go(this.index + 1));

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.go(parseInt(dot.dataset.index, 10)));
    });

    // Keyboard support
    this.addEventListener('keydown', (e) => {
      const key = e.key;
      if (key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1, { focus: false }); }
      if (key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1, { focus: false }); }
      if (key === 'Home') { e.preventDefault(); this.go(0, { focus: false }); }
      if (key === 'End') { e.preventDefault(); this.go(this.count - 1, { focus: false }); }
    });

    // Pause autoplay when user interacts
    const pause = () => this._stopAutoplay();
    this.addEventListener('pointerdown', pause, { passive: true });
    this.addEventListener('focusin', pause);

    // Pointer swipe
    this._bindPointerSwipe();
    // Resize handling
    window.addEventListener('on:breakpoint-change', () => this._apply(this.index, { focus: false }));
    window.addEventListener('resize', this._debouncedResize = this._debounce(() => this._apply(this.index, { focus: false }), 120));
  }

  _bindPointerSwipe() {
    const el = this.viewport;
    if (!el) return;

    let startX = 0;
    let dx = 0;
    let isDown = false;

    const onDown = (e) => {
      isDown = true;
      startX = e.clientX;
      dx = 0;
    };

    const onMove = (e) => {
      if (!isDown) return;
      dx = e.clientX - startX;
    };

    const onUp = () => {
      if (!isDown) return;
      isDown = false;

      // Threshold swipe
      const threshold = 40;
      if (dx > threshold) this.go(this.index - 1, { focus: false });
      if (dx < -threshold) this.go(this.index + 1, { focus: false });
    };

    el.addEventListener('pointerdown', onDown, { passive: true });
    el.addEventListener('pointermove', onMove, { passive: true });
    el.addEventListener('pointerup', onUp, { passive: true });
    el.addEventListener('pointercancel', onUp, { passive: true });

    this._unbindPointer = () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
    };
  }

  go(next, opts = {}) {
    const options = { focus: true, pauseMedia: true, ...opts };

    let target = next;
    if (this.loop) {
      target = (target + this.count) % this.count;
    } else {
      target = Math.max(0, Math.min(this.count - 1, target));
    }

    if (target === this.index) return;

if (options.pauseMedia) {
  if (typeof pauseAllMedia === 'function') {
    pauseAllMedia(this);
  } else {
    this.querySelectorAll('video').forEach((v) => v.pause());
  }
}

    this._apply(target, options);
  }

  _apply(index, { focus = true } = {}) {
    this.index = index;

    const x = -index * 100;
    this.track.style.transform = `translate3d(${x}%,0,0)`;

    // aria + visibility for slides
    this.slides.forEach((slide, i) => {
      const active = i === index;
      slide.toggleAttribute('aria-hidden', !active);
      slide.dataset.active = active ? 'true' : 'false';
    });

    // header switch
    this.headers.forEach((h, i) => {
      const active = i === index;
      h.classList.toggle('hidden', !active);
      h.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    // dots
    this.dots.forEach((dot, i) => {
      const active = i === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
      dot.tabIndex = active ? 0 : -1;
    });

    // disable arrows when not looping
    if (this.prevBtn && this.nextBtn && !this.loop) {
      this.prevBtn.disabled = index === 0;
      this.nextBtn.disabled = index === this.count - 1;
    }

    // Autoplay active slide video if present and allowed
    const activeSlide = this.slides[index];
    const video = activeSlide?.querySelector('video');

if (video) {
  const slideAllowsAutoplay = video.dataset.autoplayEnabled === 'true';
  const canAutoplay = this.videoAutoplay && slideAllowsAutoplay && !this.prefersReducedMotion;

  if (canAutoplay) {
    // Ensure it's muted (required by most browsers for autoplay)
    video.muted = true;
    video.playsInline = true;

    video.play().catch(() => {});
  } else {
    video.pause();
  }
}


    if (focus) {
      // Keep focus on active dot so keyboard users have a stable control
      const activeDot = this.dots[index];
      if (activeDot) activeDot.focus({ preventScroll: true });
    }
  }

  _startAutoplay() {
    this._stopAutoplay();
    this._timer = window.setInterval(() => this.go(this.index + 1, { focus: false }), this.autoplaySeconds * 1000);
  }

  _stopAutoplay() {
    if (this._timer) window.clearInterval(this._timer);
    this._timer = null;
  }

  _debounce(fn, wait = 200) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

if (!customElements.get('gwt-media-with-text-carousel')) {
  customElements.define('gwt-media-with-text-carousel', GwtMediaWithTextCarousel);
}
